import { VideoResolution } from '@/ai/video-models';
import { getTrendCredits, TRENDS } from '@/config/trends';
import { Routes } from '@/lib/routes';

export interface RamalamaTextLink {
  label: string;
  href: string;
}

export interface RamalamaTikTokDemo {
  /** TikTok video id, used by the official embed player. */
  id: string;
  author: string;
  url: string;
  title: string;
  caption: string;
}

export interface RamalamaCopy {
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  breadcrumbHome: string;
  breadcrumbCurrent: string;
  title: string;
  /** Trailing words of `title` shown in the green accent colour. */
  titleAccent: string;
  subtitle: string;
  quickAnswer: string;
  storyboard: { label: string; captions: [string, string, string] };
  quickFacts: { label: string; value: string }[];
  demos: {
    title: string;
    intro: string;
    items: RamalamaTikTokDemo[];
    note: string;
  };
  comparison: {
    title: string;
    intro: string;
    columns: string[];
    rows: string[][];
  };
  pain: { title: string; paragraphs: string[] };
  features: {
    title: string;
    intro: string;
    items: { title: string; body: string }[];
  };
  advantage: {
    title: string;
    items: { title: string; body: string }[];
  };
  input: { title: string; intro: string; items: string[] };
  steps: {
    title: string;
    intro: string;
    items: { name: string; text: string }[];
  };
  useCases: { title: string; columns: string[]; rows: string[][] };
  decision: {
    title: string;
    columns: string[];
    rows: { goal: string; tool: RamalamaTextLink | string; why: string }[];
  };
  fileHandling: {
    title: string;
    items: string[];
    pricingLink: RamalamaTextLink;
  };
  pricing: { title: string; intro: string };
  faqTitle: string;
  faqs: { question: string; answer: string }[];
  cta: { title: string; body: string; button: string };
  disclaimer: string;
  lastUpdatedLabel: string;
}

const TREND = TRENDS.ramalama;
const SECONDS = TREND.templateSeconds;
const SD_CREDITS = getTrendCredits(TREND, VideoResolution.P480);
const HD_CREDITS = getTrendCredits(TREND, VideoResolution.P720);

/**
 * Copy for /ramalama-trend: the Ramalama (Bang Bang) Halloween costume walk,
 * re-filmed from one photo by the trend workbench (`TRENDS.ramalama`, four
 * original 12-second 9:16 templates: solo, 2 people, 3 people, pet).
 * English only; `/es/ramalama-trend` stays `noindex` (see `src/lib/locale.ts`).
 * Trend facts: RouteNote (2026-10-09), Trends by Masterhooks (2026-10-10),
 * and Róisín Murphy's own TikTok post (2025-10-20). Product facts come from
 * `src/config/trends.ts` and `src/config/pricing-config.ts`.
 */
const RAMALAMA_COPY: { en: RamalamaCopy } = {
  en: {
    seoTitle: 'Ramalama Trend AI Video Generator: Halloween Costume Walk',
    seoDescription: `Do the Ramalama trend from one photo of you, 2-3 friends, or your pet: a slow low walk, a flash on the beat, then a witch reveal. ${SECONDS}s, 9:16.`,
    keywords: [
      'ramalama trend',
      'ramalama ai',
      'ramalama halloween trend',
      'ramalama bang bang trend',
      'ramalama costume walk',
    ],
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Ramalama Trend',
    title: 'Ramalama Trend AI Video Generator',
    titleAccent: 'AI Video Generator',
    subtitle:
      'Turn one photo of you, your friends, or your pet into the Halloween costume walk, with a witch reveal on the beat.',
    quickAnswer: `This Ramalama trend generator re-films the Halloween costume walk with the people or pet in your photo: a low camera on their legs as they walk slowly toward it, a white camera flash on the beat, then a full-body reveal in witch costumes. It follows a ${SECONDS}-second vertical template, so only who is in it changes. It makes videos for the Ramalama trend on TikTok, set to Róisín Murphy's song; it is not RamaLama, the open-source developer tool for running AI models.`,
    storyboard: {
      label:
        'Three-panel Ramalama trend storyboard: legs in jeans and sneakers walking toward a low camera on a pumpkin-lit lane, a white camera flash, then the same person revealed in a witch hat and cape',
      captions: [
        '1. The walk: camera at shoe level, slow heavy steps on a pumpkin-lit lane.',
        '2. The beat: a white camera flash fills the frame.',
        '3. The reveal: full body in a witch costume, deadpan stare, cape swish, wave.',
      ],
    },
    quickFacts: [
      {
        label: 'Input',
        value:
          'One photo: you alone, 2 or 3 people together, or one pet. JPEG, PNG, or WebP, up to 10MB.',
      },
      {
        label: 'Output',
        value: `A ${SECONDS}-second vertical 9:16 MP4 of the Ramalama trend at 480p or 720p, ready for TikTok and Reels.`,
      },
      {
        label: 'Sound',
        value:
          'No audio. Add "Ramalama (Bang Bang)" from the TikTok or Instagram music library when you post.',
      },
      {
        label: 'Cost',
        value: `${SD_CREDITS} credits at 480p, ${HD_CREDITS} credits at 720p, for every mode.`,
      },
    ],
    demos: {
      title: 'What does the Ramalama trend look like on TikTok?',
      intro:
        'The Ramalama trend opens on shoes and legs walking slowly at a low camera, then shows the whole costume when the beat drops. These creators posted their own versions; they were not made with this tool. A Ramalama trend clip from this page follows the same structure with an original template.',
      items: [
        {
          id: '7695139283392318751',
          author: 'yoyavazquez_',
          url: 'https://www.tiktok.com/@yoyavazquez_/video/7695139283392318751',
          title: 'A daylight witch walk',
          caption:
            'A solo witch walk posted in October 2026: the slow strut toward the camera, then the full costume.',
        },
        {
          id: '7694506375937854733',
          author: 'fullcatalchemist',
          url: 'https://www.tiktok.com/@fullcatalchemist/video/7694506375937854733',
          title: 'An AI cat in costume',
          caption:
            'An AI-made cat doing the Halloween walk, posted in October 2026: the pet version of the trend.',
        },
        {
          id: '7556345679170309389',
          author: 'swaggy_cucumber',
          url: 'https://www.tiktok.com/@swaggy_cucumber/video/7556345679170309389',
          title: 'A witch trio on a night street',
          caption:
            'Three friends walking as witches, posted on 1 October 2025, one of the group walks that made the trend big.',
        },
      ],
      note: 'Videos play from TikTok and belong to their creators. They load when you scroll to them.',
    },
    comparison: {
      title: 'How is an AI costume walk different from filming it yourself?',
      intro:
        'The classic Ramalama costume walk needs a costume, a phone on the floor, and someone to time the reveal. Doing the Ramalama trend here needs one photo.',
      columns: ['Question', 'This generator', 'Filming it yourself'],
      rows: [
        [
          'What you provide',
          'One photo of you, 2-3 people, or a pet',
          'A costume, a location, and several takes',
        ],
        [
          'Costume',
          'Witch costumes come from the template',
          'You buy or make one',
        ],
        [
          'Low walk and reveal',
          'Same camera, steps, and flash every run',
          'Depends on your framing and timing',
        ],
        [
          'Friends and pets',
          'Pick 2 people, 3 people, or Pet',
          'Everyone has to be there and in costume; pets rarely walk on cue',
        ],
        ['Sound', 'None; add it in the app when you post', 'Added in the app'],
      ],
    },
    pain: {
      title: 'Why is the costume walk hard to film?',
      paragraphs: [
        'The Ramalama Halloween trend looks simple: a phone at ankle height, a slow walk toward it, then the costume. In practice you need the costume itself, a spooky location, a second person or a stack of books for the phone, and several takes before the reveal lands on the beat.',
        'Group walks are harder: two or three friends have to be dressed, in step, and free at the same time. Pets are harder still, because a cat will not walk toward a phone on cue. An AI version of the Ramalama trend skips the shoot. The template already has the walk, the flash, and the costumes; your photo only sets who is in it.',
      ],
    },
    features: {
      title: 'Why make your Ramalama trend video here?',
      intro:
        'The workbench above is set up for this one walk, so a Ramalama trend video takes one photo, one mode, and one click.',
      items: [
        {
          title: 'The walk and reveal, shot for shot',
          body: `The low shoe-level camera, the slow steps, the white flash on the beat, and the witch reveal come from a ${SECONDS}-second template, so every run keeps the same timing.`,
        },
        {
          title: 'Solo, 2 people, 3 people, or a pet',
          body: 'Each mode has its own original template with the right number of walkers, or a cat for the pet version. Pick the mode, then upload one matching photo.',
        },
        {
          title: 'Vertical and ready to post',
          body: `The clip is 9:16 like a TikTok, at 480p for ${SD_CREDITS} credits or 720p HD for ${HD_CREDITS}. Every mode costs the same.`,
        },
        {
          title: 'Clear credits and automatic refunds',
          body: 'The Generate button shows the credit cost before you confirm. If a generation fails or is blocked, its credits go back to your balance.',
        },
      ],
    },
    advantage: {
      title: 'Why does the reveal still look like you, or your pet?',
      items: [
        {
          title: 'Motion copied, not guessed',
          body: 'The Ramalama trend walk here comes from a real clip, so the steps, the flash, and the reveal land at the same moment every time instead of drifting from run to run.',
        },
        {
          title: 'Faces and fur stay yours',
          body: 'The prompt keeps the faces, hair, and skin tone from your photo, matched left to right in group modes, and a pet’s breed, fur colors, and markings in Pet mode. Only the costume comes from the template.',
        },
      ],
    },
    input: {
      title: 'What photo works best for the costume walk?',
      intro:
        'The model can only keep what it can see. A clear photo gives the cleanest Ramalama trend reveal.',
      items: [
        'Solo: one person, face to the camera, full body or waist-up.',
        '2 or 3 people: one photo with everyone side by side and every face visible. People are matched left to right.',
        'Pet: one cat or dog, face and whole body in view, no costume or strong filter.',
        'Soft, even light. Sunglasses, hats over the face, or heavy filters make faces less accurate.',
        'JPEG, PNG, or WebP, up to 10MB. Only photos of yourself or of people who agreed to appear.',
      ],
    },
    steps: {
      title: 'How do I do the Ramalama trend from one photo?',
      intro: 'Five steps from one photo to a Ramalama trend clip you can post.',
      items: [
        {
          name: 'Pick a mode',
          text: 'Choose Solo, 2 people, 3 people, or Pet. The Example tab shows that template.',
        },
        {
          name: 'Upload one photo',
          text: 'Add one clear photo that matches the mode: you, your group, or your pet.',
        },
        {
          name: 'Generate',
          text: `Pick 480p (${SD_CREDITS} credits) or 720p (${HD_CREDITS}) and press Generate. Signed out, you log in first and your photo stays.`,
        },
        {
          name: 'Wait a few minutes',
          text: 'Most videos are ready in 5 to 10 minutes and are saved to your history.',
        },
        {
          name: 'Add the sound and post',
          text: 'Download the MP4, add "Ramalama (Bang Bang)" in TikTok or Instagram, and line the whisper up with the flash.',
        },
      ],
    },
    useCases: {
      title: 'Who makes the Ramalama Halloween trend, and how?',
      columns: ['Who', 'Photo', 'Mode', 'Result'],
      rows: [
        [
          'Anyone without a costume yet',
          'A clear photo of yourself',
          'Solo',
          'Your own Ramalama trend walk and witch reveal before Halloween night',
        ],
        [
          'Best friends or a costume group',
          'One photo of 2 or 3 of you',
          '2 people or 3 people',
          'A matching witch walk with everyone in it',
        ],
        [
          'Cat and dog owners',
          'One photo of your pet',
          'Pet',
          'Your pet strutting in, then in a witch hat and cape',
        ],
      ],
    },
    decision: {
      title: 'Which tool fits your version of the Ramalama trend?',
      columns: ['You want', 'Use', 'Why'],
      rows: [
        {
          goal: 'The Ramalama trend from one photo',
          tool: 'This page',
          why: 'This workbench follows the template walk shot for shot.',
        },
        {
          goal: 'A costume other than the witch',
          tool: { label: 'Reference to Video', href: Routes.ReferenceToVideo },
          why: 'It takes several stills and a prompt, and invents new motion.',
        },
        {
          goal: 'Animate an existing costume photo',
          tool: { label: 'Image to Video', href: Routes.ImageToVideo },
          why: 'It moves the photo you already have instead of recasting a walk.',
        },
        {
          goal: 'Another trend from one selfie',
          tool: {
            label: 'Devil Wears Prada AI',
            href: Routes.DevilWearsPradaAi,
          },
          why: 'A different TikTok transition on the same workbench.',
        },
        {
          goal: 'A copy of a specific creator’s video',
          tool: 'Not supported',
          why: 'The templates are original clips made for this site, not creators’ posts.',
        },
      ],
    },
    fileHandling: {
      title: 'What happens to your photo and the finished clip?',
      items: [
        'You sign in to generate, so every clip lands in your own account.',
        'Photos are checked by an automatic safety filter before generation and are only used for the video you ask for.',
        'Finished Ramalama trend clips are saved to your history, where you can watch and download them again.',
        'Generating needs credits from a one-time credit pack. There is no subscription.',
      ],
      pricingLink: {
        label: 'See credit packs',
        href: Routes.Pricing,
      },
    },
    pricing: {
      title: 'Credit packs',
      intro: `Buy credits once. They never expire, and a failed generation is refunded. One ${SECONDS}-second Ramalama trend video is ${SD_CREDITS} credits at 480p in any mode.`,
    },
    faqTitle: 'Ramalama trend questions',
    faqs: [
      {
        question: 'Is this RamaLama, the tool for running AI models?',
        answer:
          'No. If you searched for Ramalama AI to run language models, that is RamaLama, an open-source command-line tool for developers that runs AI models in containers. This page makes Halloween costume-walk videos for the Ramalama trend on TikTok, named after Róisín Murphy’s song, and it has no connection to that project.',
      },
      {
        question: 'What song is the Ramalama trend, and is it in my video?',
        answer:
          'The Ramalama Bang Bang trend uses the song "Ramalama (Bang Bang)" by Róisín Murphy, from her 2005 album Ruby Blue. Your video has no audio. Add the sound from the TikTok or Instagram music library when you post, then slide it so the whispered "takin\u2019 a picture of me" lands on the white flash.',
      },
      {
        question: 'Can I make the Ramalama trend with friends or my pet?',
        answer:
          'Yes. Pick 2 people or 3 people and upload one photo with everyone in it, side by side; the walkers are matched left to right. Pick Pet and upload one photo of your cat or dog. Each mode uses its own template with the right number of walkers.',
      },
      {
        question: 'Can I choose a costume other than the witch?',
        answer:
          'Not on this page: the witch hat and cape come from the template, which is what keeps the walk and the reveal the same every run. For another costume, use Reference to Video, which takes several stills and a prompt but makes new motion.',
      },
      {
        question: 'How much does a Ramalama trend video cost?',
        answer: `${SD_CREDITS} credits at 480p or ${HD_CREDITS} credits at 720p, in every mode. Credits come from one-time packs, starting with 50 credits for $9.90, and they never expire. There are no free credits; if a video fails, its credits are refunded automatically.`,
      },
    ],
    cta: {
      title: 'Ready to do the Ramalama trend?',
      body: 'Pick a mode, upload one photo, and the workbench above films the walk and the witch reveal before Halloween.',
      button: 'Upload your photo',
    },
    disclaimer:
      '"Ramalama (Bang Bang)" is a song by Róisín Murphy. This Ramalama trend page is an independent tool. It is not affiliated with, endorsed by, or sponsored by Róisín Murphy, her label, TikTok, the creators whose videos are embedded above, or the RamaLama open-source project. It adds no song audio. Embedded TikTok videos belong to their creators and play through TikTok’s official embed. The example clips show fictional AI-generated people and a fictional cat.',
    lastUpdatedLabel: 'Last updated',
  },
};

export function getRamalamaCopy(): RamalamaCopy {
  return RAMALAMA_COPY.en;
}
